import axios from "axios";
import { refreshAuthToken, logout } from "./authService";

const API_URLS = {
  local: "http://localhost:3000/lgportal-uat",
  staging: "https://lgportal-api.uat.dwprod.co",
  production: "https://lgportal-api.dwprod.co",
};

const API_PREFIXES = {
  local: "",
  staging: "/",
  production: "/",
};

const currentEnv = (process.env.REACT_APP_ENV || "staging") as
  | "local"
  | "staging"
  | "production";
const API_BASE_URL = API_URLS[currentEnv];
const API_PREFIX = API_PREFIXES[currentEnv];

console.log(`Using API environment: ${currentEnv}, URL: ${API_BASE_URL}`);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 60000,
});

/** Email-decision endpoints must never inherit portal auth or login redirects. */
const isPublicTemporaryAccessUrl = (url?: string) =>
  !!url &&
  /(?:^|\/)temporary-access\/(?:decision|cancel|revoke)(?:[/?]|$)/.test(url);

api.interceptors.request.use(
  (config) => {
    const isPublicTemporaryAccess = isPublicTemporaryAccessUrl(config.url);
    if (!isPublicTemporaryAccess) {
      const token = localStorage.getItem("auth_token");
      if (token) config.headers.Authorization = `${token}`;
    } else if (config.headers) {
      delete config.headers.Authorization;
    }

    if (
      config.url &&
      !config.url.startsWith("http") &&
      !config.url.startsWith("/")
    ) {
      let url = config.url;
      if (currentEnv === "local" && url.startsWith("dpp/")) {
        url = url.slice(4);
      }
      config.url = `${API_PREFIX}${url}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

const isTokenExpired = (token: string): boolean => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((character) =>
          `%${("00" + character.charCodeAt(0).toString(16)).slice(-2)}`,
        )
        .join(""),
    );
    const { exp } = JSON.parse(jsonPayload);
    return exp && exp * 1000 < Date.now() + 5 * 60 * 1000;
  } catch {
    return true;
  }
};

let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    // A customer email link remains public even in a browser holding an old
    // portal session. Let the decision page display its own neutral state.
    if (isPublicTemporaryAccessUrl(originalRequest.url)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const token = localStorage.getItem("auth_token");
      if (token && isTokenExpired(token)) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((newToken) => {
              originalRequest.headers.Authorization = `${newToken}`;
              return api(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }
        isRefreshing = true;

        try {
          const refreshResponse = await refreshAuthToken();
          originalRequest.headers.Authorization = `${refreshResponse.token}`;
          processQueue(null, refreshResponse.token);
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          logout();
          window.location.href = "/auth/signin";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }

    if (error.code === "ERR_NETWORK") {
      const token = localStorage.getItem("auth_token");
      if (token && isTokenExpired(token)) {
        try {
          await refreshAuthToken();
        } catch {
          logout();
          window.location.href = "/auth/signin";
        }
      }
      return Promise.reject(error);
    }

    if (
      error.response?.headers &&
      (error.response.headers["x-cache"] === "Error from cloudfront" ||
        error.response.headers["x-amz-errortype"] === "AccessDeniedException")
    ) {
      logout();
      window.location.href = "/auth/signin";
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default api;
